/**
 * Snapshot Formatter Section Builder
 *
 * Section construction, field-label recovery, collapse heuristics, and section
 * match bookkeeping.
 *
 * @module
 */

import { isVolatileIdentifier } from '@chatsdet/types';
import { effectiveRole, type SnapshotElement } from './snapshot-analyzer.js';
import {
  collectContextAnnotations,
  collectInfoText,
  collectLiveRegionText,
  detectContentCardRun,
  detectDatepicker,
  subtreeContainsLiveRegion,
} from './snapshot-formatter-content.js';
import { detectGrid, gridContainsRef, isWidgetTable } from './snapshot-formatter-grid.js';
import {
  INTERACTIVE_LEAF_ROLES,
  INTERACTIVE_ROLES,
  STRUCTURAL_ROLES,
  TEXT_CARRYING_ROLES,
  VISUAL_HEADING_MAX_LENGTH,
  collectComboboxOptions,
  countElementsWithRefs,
  extractDataValue,
  getDisplayText,
  getElementText,
  hasInteractiveDescendants,
  isActionableClickableGeneric,
  isActiveElement,
  isAriaDismissSentinel,
  isClickableByAttribute,
  isDisabledElement,
  isReadonlyElement,
  isMenuTriggerNoise,
  subtreeContainsActive,
  type FormattedElement,
  type Section,
} from './snapshot-formatter-shared.js';

/** Roles that can carry label text in field label candidates */
const FIELD_LABEL_TEXT_ROLES = new Set([
  'generic',
  'paragraph',
  'text',
  'strong',
  'emphasis',
  'label',
  'heading',
]);

const MIN_ELEMENTS_TO_COLLAPSE = 20;
const DIALOG_ROLES = new Set(['dialog', 'alertdialog']);
const ROW_TOGGLE_ROLES = new Set(['checkbox', 'radio', 'switch']);

/**
 * Per-tree state populated by `findDismissSentinelPairs` and consumed by
 * `buildSection` / `collectInteractiveElements`. Reset at the start of each
 * `buildSectionTree` call.
 *
 * - `sentinelRefsToSkip`: refs of React Aria dismiss sentinels we have
 *   paired to a dialog. These must not be emitted as standalone interactive
 *   elements anywhere in the rendered tree.
 * - `dialogDismissByRef`: maps a dialog's ref to the sentinel ref that
 *   should appear as `[dismiss=<ref>]` on the dialog heading.
 */
let sentinelRefsToSkip: Set<string> = new Set();
let dialogDismissByRef: Map<string, string> = new Map();
let rowToggleRefsToSkip: Set<string> = new Set();

/**
 * Walk the parse tree and pair React Aria dismiss-sentinel buttons with
 * their adjacent active dialog. The structural fingerprint (empty button
 * named "Dismiss" sitting immediately before/after a dialog whose subtree
 * contains the active element) is specific to the framework pattern; if it
 * doesn't match we leave the button alone so we never strip a ref the
 * agent might legitimately need.
 */
function findDismissSentinelPairs(
  elements: SnapshotElement[],
  sentinels: Set<string>,
  byDialog: Map<string, string>
): void {
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (!isAriaDismissSentinel(el) || !el.ref) {
      findDismissSentinelPairs(el.children, sentinels, byDialog);
      continue;
    }
    const candidates = [elements[i - 1], elements[i + 1]];
    for (const candidate of candidates) {
      if (!candidate || !candidate.ref) continue;
      if (!DIALOG_ROLES.has(effectiveRole(candidate))) continue;
      if (!subtreeContainsActive(candidate)) continue;
      sentinels.add(el.ref);
      byDialog.set(candidate.ref, el.ref);
      break;
    }
  }
}

/**
 * Detect if a generic element acts as a visual heading.
 */
function isVisualHeading(
  element: SnapshotElement,
  siblings: SnapshotElement[],
  siblingIndex: number
): boolean {
  if (element.role !== 'generic' && element.role !== 'paragraph') return false;
  if (isClickableByAttribute(element)) return false;

  const text = getVisualHeadingText(element);
  if (!text || text.length < 3 || text.length > VISUAL_HEADING_MAX_LENGTH) return false;
  if (hasInteractiveDescendants(element)) return false;

  let interactiveCount = 0;
  for (let i = siblingIndex + 1; i < siblings.length; i++) {
    const sib = siblings[i];
    if (sib.role === 'heading' || STRUCTURAL_ROLES.has(effectiveRole(sib))) break;
    if (sib.role === 'generic' || sib.role === 'paragraph') {
      if (!isClickableByAttribute(sib) && !hasInteractiveDescendants(sib)) {
        const sibText = getVisualHeadingText(sib);
        if (sibText && sibText.length >= 3 && sibText.length <= VISUAL_HEADING_MAX_LENGTH) {
          break;
        }
      }
    }
    if (hasInteractiveDescendants(sib) || isClickableByAttribute(sib)) {
      interactiveCount++;
    }
  }

  return interactiveCount >= 3;
}

function getVisualHeadingText(element: SnapshotElement): string | undefined {
  const direct = element.text;
  if (direct) return direct;
  for (const child of element.children) {
    if (TEXT_CARRYING_ROLES.has(child.role) && child.text) return child.text;
  }
  return undefined;
}

function findNextSectionBoundary(siblings: SnapshotElement[], startIndex: number): number {
  for (let i = startIndex; i < siblings.length; i++) {
    const sib = siblings[i];
    if (sib.role === 'heading' || STRUCTURAL_ROLES.has(effectiveRole(sib))) return i;
    if (isVisualHeading(sib, siblings, i)) return i;
  }
  return siblings.length;
}

function isFieldLabelCandidate(element: SnapshotElement): boolean {
  if (
    element.role !== 'generic' &&
    element.role !== 'paragraph' &&
    element.role !== 'text' &&
    element.role !== 'label' &&
    element.role !== 'strong' &&
    element.role !== 'emphasis'
  ) {
    return false;
  }
  if (isClickableByAttribute(element)) return false;
  if (hasInteractiveDescendants(element)) return false;
  const text = getFieldLabelText(element);
  if (!text || text.length < 1 || text.length > VISUAL_HEADING_MAX_LENGTH) return false;
  return true;
}

function getFieldLabelText(element: SnapshotElement): string | undefined {
  if (element.text) return element.text.trim();
  if (element.inputValue && !INTERACTIVE_ROLES.has(element.role)) return element.inputValue.trim();
  for (const child of element.children) {
    if (FIELD_LABEL_TEXT_ROLES.has(child.role)) {
      if (child.text) return child.text.trim();
      if (child.inputValue && !INTERACTIVE_ROLES.has(child.role)) return child.inputValue.trim();
    }
  }
  return undefined;
}

function resolveContainerFieldLabel(container: SnapshotElement): string | undefined {
  const kids = container.children;
  if (kids.length < 2 || kids.length > 4) return undefined;

  const firstChild = kids[0];
  if (isFieldLabelCandidate(firstChild)) {
    return getFieldLabelText(firstChild);
  }
  return undefined;
}

function findFieldLabelFromSiblings(
  siblings: SnapshotElement[],
  interactiveIndex: number
): string | undefined {
  for (let i = interactiveIndex - 1; i >= 0; i--) {
    const sib = siblings[i];
    if (
      INTERACTIVE_ROLES.has(sib.role) ||
      STRUCTURAL_ROLES.has(effectiveRole(sib)) ||
      sib.role === 'heading'
    ) {
      break;
    }
    if (isClickableByAttribute(sib)) {
      if (sib.role === 'generic' || sib.role === 'paragraph' || sib.role === 'label') {
        for (let j = i - 1; j >= 0; j--) {
          const prev = siblings[j];
          if (
            INTERACTIVE_ROLES.has(prev.role) ||
            isClickableByAttribute(prev) ||
            STRUCTURAL_ROLES.has(effectiveRole(prev)) ||
            prev.role === 'heading'
          ) {
            break;
          }
          if (isFieldLabelCandidate(prev)) {
            return getFieldLabelText(prev);
          }
          if (prev.children.length >= 1 && prev.children.length <= 4) {
            const containerLabel = resolveContainerFieldLabel(prev);
            if (containerLabel) return containerLabel;
          }
          if (prev.children.length > 4) break;
        }
      }
      break;
    }
    if (isFieldLabelCandidate(sib)) {
      return getFieldLabelText(sib);
    }
    if (sib.children.length >= 1 && sib.children.length <= 4) {
      const containerLabel = resolveContainerFieldLabel(sib);
      if (containerLabel) return containerLabel;
    }
    if (sib.children.length > 4) break;
  }
  return undefined;
}

function findFieldLabelInContext(
  _element: SnapshotElement,
  siblings: SnapshotElement[],
  siblingIndex: number
): string | undefined {
  return findFieldLabelFromSiblings(siblings, siblingIndex);
}

function shouldDropInheritedFieldLabel(
  element: SnapshotElement,
  inheritedFieldLabel: string | undefined
): boolean {
  if (!inheritedFieldLabel) return false;
  if (INTERACTIVE_LEAF_ROLES.has(element.role)) return false;
  if (!isClickableByAttribute(element)) return false;

  const directText = getElementText(element);
  if (directText) {
    return directText.toLowerCase() !== inheritedFieldLabel.toLowerCase();
  }

  const childTexts: string[] = [];
  for (const child of element.children) {
    if (!INTERACTIVE_LEAF_ROLES.has(child.role)) {
      const text = getElementText(child);
      if (text && TEXT_CARRYING_ROLES.has(child.role)) {
        childTexts.push(text);
      }
    }
  }
  const composedText = childTexts.join(' ');
  if (!composedText) return false;

  return composedText.toLowerCase() !== inheritedFieldLabel.toLowerCase();
}

function isExpandedElement(element: SnapshotElement): boolean {
  return (
    element.attributes['expanded'] === 'true' || element.attributes['aria-expanded'] === 'true'
  );
}

function collectExpandedChildren(element: SnapshotElement, result: FormattedElement[]): void {
  for (const child of element.children) {
    if (STRUCTURAL_ROLES.has(effectiveRole(child))) continue;

    if (INTERACTIVE_LEAF_ROLES.has(child.role) && child.ref) {
      result.push(formatElementWithNestedToggle(child));
      continue;
    }

    if (child.role === 'listitem' && child.ref) {
      const text =
        child.text ||
        child.children
          .map((c) => getElementText(c))
          .filter((value): value is string => Boolean(value))
          .join(' | ') ||
        getDisplayText(child);
      if (text && text !== 'listitem') {
        result.push(formatElementWithNestedToggle(child, { overrideLabel: text }));
        continue;
      }
    }

    if (isClickableByAttribute(child) && child.ref) {
      result.push(formatElementWithNestedToggle(child));
    }

    collectExpandedChildren(child, result);
  }
}

function hasActionableChildren(element: SnapshotElement): boolean {
  const children: FormattedElement[] = [];
  collectExpandedChildren(element, children);
  return children.length > 0;
}

function isExpandedWithActionableChildren(element: SnapshotElement): boolean {
  if (!isExpandedElement(element)) return false;
  if (element.role === 'combobox') return false;
  return hasActionableChildren(element);
}

function composeTriggerLabel(element: SnapshotElement): string {
  const ariaLabel = element.attributes['aria-label'];
  if (ariaLabel) return ariaLabel;

  const fullText = element.text || '';
  if (fullText) {
    const leafTexts: string[] = [];
    function collectLeafTexts(el: SnapshotElement): void {
      for (const child of el.children) {
        const text = getElementText(child)?.trim();
        if (text) {
          leafTexts.push(text);
        }
        collectLeafTexts(child);
      }
    }
    collectLeafTexts(element);

    if (leafTexts.length > 0) {
      let stripped = fullText;
      for (const lt of leafTexts) {
        stripped = stripped.replace(lt, '');
      }
      stripped = stripped.replace(/\s+/g, ' ').trim();
      if (stripped) return stripped;
    }
  }

  return element.role;
}

function formatElement(
  element: SnapshotElement,
  options?: { overrideLabel?: string; fieldLabel?: string }
): FormattedElement {
  const isClickable = isActionableClickableGeneric(element);
  const textSource = getDisplayText(element, { overrideLabel: options?.overrideLabel });
  const extracted = extractDataValue(textSource);
  const normalizedAttributes = Object.entries(element.attributes)
    .filter(
      ([key]) =>
        key !== 'ref' &&
        key !== 'cursor' &&
        key !== 'aria-controls' &&
        key !== 'aria-owns' &&
        key !== 'options' &&
        key !== 'field'
    )
    .map(([key, val]) => {
      if (key === 'aria-haspopup') return 'haspopup';
      if (key === 'aria-expanded') return `expanded=${val}`;
      if (val === 'true' || val === '') return key;
      return `${key}=${val}`;
    })
    .filter((attr) => {
      if (!extracted.label || !attr.startsWith('title=')) return true;
      const titleValue = attr.slice('title='.length).trim().toLowerCase();
      return titleValue !== extracted.label.trim().toLowerCase();
    });
  normalizedAttributes.push(...renderableTestIdAttributes(element, textSource));

  let comboboxOptions = element.role === 'combobox' ? collectComboboxOptions(element) : undefined;
  if (!comboboxOptions && element.attributes['options']) {
    comboboxOptions = element.attributes['options']
      .split('|')
      .map((o) => o.trim())
      .filter(Boolean);
  }

  return {
    ref: element.ref,
    role: effectiveRole(element),
    name: textSource,
    fieldLabel: options?.fieldLabel || element.attributes['field'] || undefined,
    label: extracted.label || undefined,
    value: extracted.value,
    status: extracted.status,
    inputValue: element.inputValue,
    options: comboboxOptions,
    isActive: isActiveElement(element),
    isDisabled: isDisabledElement(element),
    isReadonly: isReadonlyElement(element),
    isClickable,
    attributes: normalizedAttributes,
  };
}

function collectNestedRowToggles(element: SnapshotElement, result: SnapshotElement[]): void {
  for (const child of element.children) {
    if (ROW_TOGGLE_ROLES.has(child.role) && child.ref) {
      result.push(child);
      continue;
    }
    if (INTERACTIVE_LEAF_ROLES.has(child.role)) continue;
    collectNestedRowToggles(child, result);
  }
}

function getNestedRowToggles(element: SnapshotElement): SnapshotElement[] {
  const toggles: SnapshotElement[] = [];
  collectNestedRowToggles(element, toggles);
  return toggles;
}

function isMeaningfulClickableToggleRow(element: SnapshotElement): boolean {
  if (!isActionableClickableGeneric(element)) return false;
  const label = getDisplayText(element);
  if (!label || label === element.role) return false;
  return getNestedRowToggles(element).length > 0;
}

function isMeaningfulDisabledControlWithoutRef(element: SnapshotElement): boolean {
  if (element.ref) return false;
  if (!INTERACTIVE_LEAF_ROLES.has(element.role)) return false;
  if (!isDisabledElement(element)) return false;

  const label = getDisplayText(element);
  if (!label || label === element.role || label === '(icon)') return false;
  return true;
}

function hasUsefulRenderLabel(label: string, role: string): boolean {
  const trimmed = label.trim();
  return Boolean(trimmed && trimmed !== role && trimmed !== '(icon)' && trimmed !== '(no label)');
}

function renderableTestIdAttributes(element: SnapshotElement, label: string): string[] {
  const testIds = element.cssHints?.testIds;
  if (!testIds?.length) return [];

  const expose =
    isActionableClickableGeneric(element) || !hasUsefulRenderLabel(label, effectiveRole(element));
  if (!expose) return [];

  const uniqueStableIds: string[] = [];
  for (const id of testIds) {
    const trimmed = id.trim();
    if (!trimmed || trimmed.length > 80 || isVolatileIdentifier(trimmed)) continue;
    if (!uniqueStableIds.includes(trimmed)) uniqueStableIds.push(trimmed);
  }

  return uniqueStableIds.slice(0, 2).map((id) => `testid=${id}`);
}

function toggleStateAttribute(toggle: SnapshotElement): string | undefined {
  if (toggle.attributes['checked'] === undefined) return undefined;
  const checked = toggle.attributes['checked'];
  if (checked === '' || checked === 'true') return 'checked';
  return `checked=${checked}`;
}

function formatElementWithNestedToggle(
  element: SnapshotElement,
  options?: { overrideLabel?: string; fieldLabel?: string }
): FormattedElement {
  const formatted = formatElement(element, options);
  if (!isMeaningfulClickableToggleRow(element)) return formatted;

  const toggles = getNestedRowToggles(element);
  for (const toggle of toggles) {
    if (toggle.ref) rowToggleRefsToSkip.add(toggle.ref);
  }

  const state = toggleStateAttribute(toggles[0]);
  if (
    state &&
    !formatted.attributes.some((attr) => attr === 'checked' || attr.startsWith('checked='))
  ) {
    formatted.attributes.push(state);
  }

  return formatted;
}

function containsNavigation(section: Section): boolean {
  for (const sub of section.subsections) {
    if (sub.role === 'navigation') return true;
    if (containsNavigation(sub)) return true;
  }
  return false;
}

function shouldCollapse(section: Section): boolean {
  if (section.containsActive) return false;
  if (section.hasSearchMatch) return false;
  // Role-based exceptions — these must come BEFORE the background check
  // so that navigation, dialogs, banners, and forms stay visible even when
  // they're in a background region of the page.
  if (section.role === 'dialog' || section.role === 'alertdialog') return false;
  if (section.role === 'navigation' || section.role === 'menubar' || section.role === 'menu')
    return false;
  if (section.role === 'banner') return false;
  if (section.role === 'form') return false;
  // Don't collapse sections that contain navigation (e.g. complementary sidebar)
  if (containsNavigation(section)) return false;
  if (section.isBackground) return true;
  if (section.elementCount >= MIN_ELEMENTS_TO_COLLAPSE) return true;
  return false;
}

function deduplicateFieldLabels(elements: FormattedElement[]): void {
  const byFieldLabel = new Map<string, FormattedElement[]>();
  for (const el of elements) {
    if (!el.fieldLabel) continue;
    const existing = byFieldLabel.get(el.fieldLabel);
    if (existing) {
      existing.push(el);
    } else {
      byFieldLabel.set(el.fieldLabel, [el]);
    }
  }

  for (const [, group] of byFieldLabel) {
    if (group.length < 2) continue;

    for (let i = 0; i < group.length; i++) {
      const el = group[i];
      if (el.name && el.name !== el.fieldLabel) {
        el.fieldLabel = `${el.fieldLabel}: ${el.name}`;
      } else {
        el.fieldLabel = `${el.fieldLabel} (${i + 1})`;
      }
    }
  }
}

function buildGridSection(element: SnapshotElement): Section {
  const gridInfo = detectGrid(element);
  if (!gridInfo) {
    throw new Error('buildGridSection requires a grid element');
  }
  return {
    ref: element.ref || '',
    role: effectiveRole(element),
    heading: 'Grid',
    level: 0,
    elements: [],
    subsections: [],
    containsActive: gridInfo.containsActive,
    collapsed: false,
    isBackground: false,
    isFocused: false,
    elementCount: gridInfo.totalRows * Math.max(gridInfo.columns.length, 1),
    gridInfo,
  };
}

function collectInteractiveElements(
  element: SnapshotElement,
  elements: FormattedElement[],
  subsections: Section[],
  depth: number,
  inheritedFieldLabel?: string
): void {
  // Skip React Aria dismiss sentinels that have already been paired with a
  // dialog — they surface as `[dismiss=<ref>]` on the dialog heading instead.
  if (
    element.ref &&
    (sentinelRefsToSkip.has(element.ref) || rowToggleRefsToSkip.has(element.ref))
  ) {
    return;
  }
  if (
    (INTERACTIVE_LEAF_ROLES.has(element.role) || isClickableByAttribute(element)) &&
    element.ref
  ) {
    const effectiveFieldLabel = shouldDropInheritedFieldLabel(element, inheritedFieldLabel)
      ? undefined
      : inheritedFieldLabel;

    if (INTERACTIVE_LEAF_ROLES.has(element.role) && isExpandedWithActionableChildren(element)) {
      elements.push(
        formatElementWithNestedToggle(element, {
          overrideLabel: composeTriggerLabel(element),
          fieldLabel: effectiveFieldLabel,
        })
      );
      collectExpandedChildren(element, elements);
      return;
    }

    if (!isMenuTriggerNoise(element)) {
      elements.push(formatElementWithNestedToggle(element, { fieldLabel: effectiveFieldLabel }));
    }

    if (!INTERACTIVE_LEAF_ROLES.has(element.role) && isClickableByAttribute(element)) {
      for (const child of element.children) {
        collectInteractiveElements(child, elements, subsections, depth + 1, effectiveFieldLabel);
      }
    }
    return;
  }

  if (isMeaningfulDisabledControlWithoutRef(element)) {
    elements.push(formatElementWithNestedToggle(element, { fieldLabel: inheritedFieldLabel }));
    return;
  }

  if (
    element.role === 'heading' ||
    STRUCTURAL_ROLES.has(effectiveRole(element)) ||
    element.role === 'grid' ||
    element.role === 'treegrid' ||
    (element.role === 'table' && !isWidgetTable(element))
  ) {
    const section = buildSection(element, depth);
    if (section) {
      subsections.push(section);
    }
    return;
  }

  const kids = element.children;

  // Detect datepicker pattern among children
  const datepickerResult = detectDatepicker(element);
  if (datepickerResult) {
    // Collect context annotations from remaining non-day children (summaries, etc.)
    const annotations = collectContextAnnotations(kids);
    subsections.push({
      heading: 'Calendar',
      ref: datepickerResult.ref,
      role: 'generic',
      level: 4,
      elements: [],
      subsections: [],
      collapsed: false,
      containsActive: false,
      isBackground: false,
      isFocused: false,
      elementCount: datepickerResult.weeks.reduce((sum, w) => sum + w.days.length, 0),
      datepickerInfo: datepickerResult,
      contextAnnotations: annotations.length > 0 ? annotations : undefined,
    });
    return;
  }

  // Detect content card runs among children — but never inside a dialog,
  // where the body should always render as full interactive content.
  if (!DIALOG_ROLES.has(effectiveRole(element))) {
    const cardRun = detectContentCardRun(kids, 0);
    if (cardRun) {
      const annotations = collectContextAnnotations(kids);
      subsections.push({
        heading: 'Content',
        ref: element.ref || '',
        role: 'generic',
        level: 4,
        elements: [],
        subsections: [],
        collapsed: false,
        containsActive: false,
        isBackground: false,
        isFocused: false,
        elementCount: cardRun.cards.length,
        contentCards: cardRun.cards,
        contextAnnotations: annotations.length > 0 ? annotations : undefined,
      });
      // Continue processing remaining children after the card run
      for (let ki = cardRun.endIndex; ki < kids.length; ki++) {
        const child = kids[ki];
        collectInteractiveElements(child, elements, subsections, depth + 1, inheritedFieldLabel);
      }
      return;
    }
  }

  for (let ki = 0; ki < kids.length; ki++) {
    const child = kids[ki];
    if (child.ref && (sentinelRefsToSkip.has(child.ref) || rowToggleRefsToSkip.has(child.ref))) {
      continue;
    }
    if (isVisualHeading(child, kids, ki)) {
      const headingText = getVisualHeadingText(child);
      const sectionElements: FormattedElement[] = [];
      const sectionSubsections: Section[] = [];
      const nextBoundary = findNextSectionBoundary(kids, ki + 1);
      for (let si = ki + 1; si < nextBoundary; si++) {
        collectInteractiveElements(kids[si], sectionElements, sectionSubsections, depth + 2);
      }
      if (sectionElements.length > 0 || sectionSubsections.length > 0) {
        const totalElements =
          sectionElements.length + sectionSubsections.reduce((sum, s) => sum + s.elementCount, 0);
        subsections.push({
          heading: headingText ?? '',
          ref: child.ref,
          role: 'heading',
          level: 4,
          elements: sectionElements,
          subsections: sectionSubsections,
          collapsed: false,
          containsActive: false,
          isBackground: false,
          isFocused: false,
          elementCount: totalElements,
        });
        ki = nextBoundary - 1;
      } else {
        collectInteractiveElements(child, elements, subsections, depth + 1);
      }
    } else if (
      (INTERACTIVE_LEAF_ROLES.has(child.role) || isClickableByAttribute(child)) &&
      child.ref
    ) {
      const rawFieldLabel = findFieldLabelInContext(child, kids, ki) ?? inheritedFieldLabel;
      const fieldLabel = shouldDropInheritedFieldLabel(child, rawFieldLabel)
        ? undefined
        : rawFieldLabel;
      if (!isMenuTriggerNoise(child)) {
        if (INTERACTIVE_LEAF_ROLES.has(child.role) && isExpandedWithActionableChildren(child)) {
          elements.push(
            formatElementWithNestedToggle(child, {
              overrideLabel: composeTriggerLabel(child),
              fieldLabel,
            })
          );
          collectExpandedChildren(child, elements);
        } else {
          elements.push(formatElementWithNestedToggle(child, { fieldLabel }));
        }
      }
      if (!INTERACTIVE_LEAF_ROLES.has(child.role) && isClickableByAttribute(child)) {
        for (const grandchild of child.children) {
          collectInteractiveElements(grandchild, elements, subsections, depth + 1, fieldLabel);
        }
      }
    } else if (isMeaningfulDisabledControlWithoutRef(child)) {
      const fieldLabel = findFieldLabelInContext(child, kids, ki) ?? inheritedFieldLabel;
      elements.push(formatElementWithNestedToggle(child, { fieldLabel }));
    } else {
      const containerLabel =
        resolveContainerFieldLabel(child) ??
        (child.role === 'table' ? findFieldLabelFromSiblings(kids, ki) : undefined) ??
        inheritedFieldLabel;
      collectInteractiveElements(child, elements, subsections, depth + 1, containerLabel);
    }
  }
}

function buildSection(element: SnapshotElement, depth: number): Section | null {
  // Sentinel refs paired with a dialog are surfaced via the dialog's
  // `[dismiss=<ref>]` annotation; skip emitting a section for them.
  if (
    element.ref &&
    (sentinelRefsToSkip.has(element.ref) || rowToggleRefsToSkip.has(element.ref))
  ) {
    return null;
  }

  const gridInfo = detectGrid(element);
  if (gridInfo) {
    return buildGridSection(element);
  }

  // Detect datepicker pattern among direct children
  const datepickerResult = detectDatepicker(element);
  if (datepickerResult) {
    const annotations = collectContextAnnotations(element.children);
    return {
      ref: element.ref || '',
      role: effectiveRole(element),
      heading: element.text || 'Calendar',
      level: 0,
      elements: [],
      subsections: [],
      containsActive: false,
      collapsed: false,
      isBackground: false,
      isFocused: false,
      elementCount: datepickerResult.weeks.reduce((sum, w) => sum + w.days.length, 0),
      datepickerInfo: datepickerResult,
      contextAnnotations: annotations.length > 0 ? annotations : undefined,
    };
  }

  // Detect content card run among direct children — but never inside a
  // dialog, where the body should always render as full interactive content.
  if (!DIALOG_ROLES.has(effectiveRole(element))) {
    const cardRun = detectContentCardRun(element.children, 0);
    if (cardRun) {
      const annotations = collectContextAnnotations(element.children);
      const leftoverElements: FormattedElement[] = [];
      const leftoverSubsections: Section[] = [];
      // Render any siblings after the card run as normal interactive content,
      // so a header card followed by buttons doesn't silently drop the buttons.
      for (let ki = cardRun.endIndex; ki < element.children.length; ki++) {
        collectInteractiveElements(
          element.children[ki],
          leftoverElements,
          leftoverSubsections,
          depth + 1
        );
      }
      const totalCount =
        cardRun.cards.length +
        leftoverElements.length +
        leftoverSubsections.reduce((sum, s) => sum + s.elementCount, 0);
      return {
        ref: element.ref || '',
        role: effectiveRole(element),
        heading: element.text || 'Content',
        level: 0,
        elements: leftoverElements,
        subsections: leftoverSubsections,
        containsActive: subtreeContainsActive(element),
        collapsed: false,
        isBackground: false,
        isFocused: false,
        elementCount: totalCount,
        contentCards: cardRun.cards,
        contextAnnotations: annotations.length > 0 ? annotations : undefined,
      };
    }
  }

  const isHeading = element.role === 'heading';
  const isStructural = STRUCTURAL_ROLES.has(effectiveRole(element));

  if (isHeading && !element.text?.trim() && element.children.length === 0) {
    return null;
  }

  let level = 0;
  if (isHeading) {
    level = element.level || parseInt(element.attributes['level'] || '2', 10);
  } else if (isStructural) {
    level = 0;
  }

  const interactiveElements: FormattedElement[] = [];
  const subsections: Section[] = [];

  const children = element.children;
  for (let ci = 0; ci < children.length; ci++) {
    const child = children[ci];
    if (child.ref && (sentinelRefsToSkip.has(child.ref) || rowToggleRefsToSkip.has(child.ref))) {
      continue;
    }
    if (child.role === 'heading' || STRUCTURAL_ROLES.has(effectiveRole(child))) {
      const childSection = buildSection(child, depth + 1);
      if (childSection) {
        subsections.push(childSection);
      }
    } else if (isVisualHeading(child, children, ci)) {
      const headingText = getVisualHeadingText(child);
      const sectionElements: FormattedElement[] = [];
      const sectionSubsections: Section[] = [];
      const nextSectionStart = findNextSectionBoundary(children, ci + 1);
      for (let si = ci + 1; si < nextSectionStart; si++) {
        collectInteractiveElements(children[si], sectionElements, sectionSubsections, depth + 2);
      }
      if (sectionElements.length > 0 || sectionSubsections.length > 0) {
        const totalElements =
          sectionElements.length + sectionSubsections.reduce((sum, s) => sum + s.elementCount, 0);
        subsections.push({
          heading: headingText ?? '',
          ref: child.ref,
          role: 'heading',
          level: 4,
          elements: sectionElements,
          subsections: sectionSubsections,
          collapsed: false,
          containsActive: false,
          isBackground: false,
          isFocused: false,
          elementCount: totalElements,
        });
        ci = nextSectionStart - 1;
      } else {
        collectInteractiveElements(child, interactiveElements, subsections, depth + 1);
      }
    } else if (
      (INTERACTIVE_LEAF_ROLES.has(child.role) || isClickableByAttribute(child)) &&
      child.ref
    ) {
      const fieldLabel = findFieldLabelInContext(child, children, ci);
      if (INTERACTIVE_LEAF_ROLES.has(child.role) && isExpandedWithActionableChildren(child)) {
        interactiveElements.push(
          formatElementWithNestedToggle(child, {
            overrideLabel: composeTriggerLabel(child),
            fieldLabel,
          })
        );
        collectExpandedChildren(child, interactiveElements);
      } else {
        if (!isMenuTriggerNoise(child)) {
          interactiveElements.push(formatElementWithNestedToggle(child, { fieldLabel }));
        }
        if (!INTERACTIVE_LEAF_ROLES.has(child.role) && isClickableByAttribute(child)) {
          for (const grandchild of child.children) {
            collectInteractiveElements(
              grandchild,
              interactiveElements,
              subsections,
              depth + 1,
              fieldLabel
            );
          }
        }
      }
    } else if (isMeaningfulDisabledControlWithoutRef(child)) {
      const fieldLabel = findFieldLabelInContext(child, children, ci);
      interactiveElements.push(formatElementWithNestedToggle(child, { fieldLabel }));
    } else {
      const containerLabel =
        resolveContainerFieldLabel(child) ?? findFieldLabelFromSiblings(children, ci);
      collectInteractiveElements(
        child,
        interactiveElements,
        subsections,
        depth + 1,
        containerLabel
      );
    }
  }

  if (isClickableByAttribute(element) && element.ref && !INTERACTIVE_ROLES.has(element.role)) {
    interactiveElements.unshift(formatElementWithNestedToggle(element));
  }

  if (!isHeading && !isStructural && interactiveElements.length === 0 && subsections.length === 0) {
    return null;
  }

  const containsActive = subtreeContainsActive(element);
  const elementCount = countElementsWithRefs(element);

  const section: Section = {
    ref: element.ref || '',
    role: effectiveRole(element),
    heading:
      element.text || (isClickableByAttribute(element) ? getDisplayText(element) : element.role),
    level,
    elements: interactiveElements,
    subsections,
    containsActive,
    collapsed: false,
    isBackground: false,
    isFocused: false,
    elementCount,
  };

  if (DIALOG_ROLES.has(effectiveRole(element)) && element.ref) {
    const dismissRef = dialogDismissByRef.get(element.ref);
    if (dismissRef) {
      section.dismissActionRef = dismissRef;
    }
    // A dialog only blocks the page when it's actually rendered. Augmentation
    // tags collapsed/off-screen dialogs `dialogInert`; capture may also mark
    // them hidden/off-screen via `visibility`. Either signal downgrades the
    // dialog so a chat/announcer widget that left `aria-modal=true` on doesn't
    // demote the whole page to background or crowd the DIALOG STACK. Absent
    // both signals, behavior is unchanged (trust `aria-modal`).
    const inert =
      element.dialogInert === true ||
      element.visibility === 'hidden' ||
      element.visibility === 'off-screen';
    if (inert) {
      section.isInertDialog = true;
    }
    if (element.attributes['aria-modal'] === 'true' && !inert) {
      section.isBlockingModal = true;
    }
  }

  const exclude = new Set<string>();
  if (section.heading) exclude.add(section.heading);
  for (const sub of subsections) {
    if (sub.heading) exclude.add(sub.heading);
  }

  // For dialog/alertdialog sections, surface non-interactive descriptive
  // text (descriptions, helper paragraphs) as context annotations. For
  // ordinary page sections, preserve only live-region feedback such as
  // validation alerts so agents do not miss why an action is unavailable.
  const infoText = DIALOG_ROLES.has(effectiveRole(element))
    ? collectInfoText(element, exclude)
    : subtreeContainsLiveRegion(element)
      ? collectLiveRegionText(element, exclude)
      : [];
  if (infoText.length > 0) {
    section.contextAnnotations = section.contextAnnotations
      ? [...section.contextAnnotations, ...infoText]
      : infoText;
  }

  deduplicateFieldLabels(interactiveElements);
  section.collapsed = shouldCollapse(section);

  return section;
}

/**
 * Find the deepest blocking-modal section in the tree along with the chain
 * of ancestor sections leading to it. Returns null when no verifiably
 * blocking modal is present. "Deepest" wins so stacked modals focus on the
 * topmost.
 */
function findBlockingModal(
  sections: Section[]
): { modal: Section; ancestors: Set<Section> } | null {
  type Best = { modal: Section; ancestors: Set<Section>; depth: number };
  let best: Best | null = null;
  const walk = (list: Section[], path: Section[]): void => {
    for (const s of list) {
      if (s.isBlockingModal) {
        const depth = path.length;
        if (!best || depth >= best.depth) {
          best = { modal: s, ancestors: new Set(path), depth };
        }
      }
      walk(s.subsections, [...path, s]);
    }
  };
  walk(sections, []);
  if (!best) return null;
  const result: Best = best;
  return { modal: result.modal, ancestors: result.ancestors };
}

/**
 * Apply background-suppression for a verified blocking modal. Sections
 * outside the modal's ancestor chain become collapsed-background. The
 * ancestor chain itself stays structural so the modal section still
 * renders, but its sibling-level interactive elements are dropped — those
 * live in the inert page shell and are unreachable while the modal is open.
 */
function markBackgroundForBlockingModal(
  sections: Section[],
  modal: Section,
  ancestors: Set<Section>
): void {
  for (const s of sections) {
    if (s === modal) continue;
    const onPath = ancestors.has(s);
    if (onPath) {
      // Drop direct interactive elements on the path — they're outside the
      // modal subtree and the live page is inert.
      s.elements = [];
      // Recurse so siblings of the modal at this depth get suppressed too.
      markBackgroundForBlockingModal(s.subsections, modal, ancestors);
      continue;
    }
    s.isBackground = true;
    s.collapsed = shouldCollapse(s);
    // Don't recurse — the whole subtree is collapsed background.
  }
}

function markBackgroundSections(sections: Section[]): void {
  const blocking = findBlockingModal(sections);
  if (blocking) {
    markBackgroundForBlockingModal(sections, blocking.modal, blocking.ancestors);
    return;
  }

  const hasActiveSibling = sections.some((s) => s.containsActive);

  if (hasActiveSibling) {
    // Find the last section containing the active element (DOM order).
    // Dialogs appearing after this index are visually on top (portals append
    // later) and should not be marked as background.
    let lastActiveIndex = -1;
    for (let i = sections.length - 1; i >= 0; i--) {
      if (sections[i].containsActive) {
        lastActiveIndex = i;
        break;
      }
    }

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      if (!section.containsActive) {
        if (DIALOG_ROLES.has(section.role) && i > lastActiveIndex) {
          continue;
        }
        section.isBackground = true;
        section.collapsed = shouldCollapse(section);
      }
    }
  }

  for (const section of sections) {
    markBackgroundSections(section.subsections);
  }
}

/**
 * Merge filter dropdown pairs where a value element and a dropdown icon
 * appear as separate elements with the field label on the wrong one.
 *
 * Pattern: element[i] is `generic [clickable]` followed by element[i+1]
 * `img [clickable]` where the img has no meaningful label (just "img" or
 * empty). The img is a decorative dropdown arrow — merge it into the
 * preceding generic and transfer its fieldLabel if the generic lacks one.
 */
function mergeFilterDropdownPairs(elements: FormattedElement[]): void {
  for (let i = elements.length - 2; i >= 0; i--) {
    const current = elements[i];
    const next = elements[i + 1];
    if (
      current.role === 'generic' &&
      current.isClickable &&
      next.role === 'img' &&
      next.isClickable &&
      (!next.label || next.label === 'img')
    ) {
      if (!current.fieldLabel && next.fieldLabel) {
        current.fieldLabel = next.fieldLabel;
      }
      elements.splice(i + 1, 1);
    }
  }
}

function applyFilterMerge(sections: Section[]): void {
  for (const section of sections) {
    mergeFilterDropdownPairs(section.elements);
    applyFilterMerge(section.subsections);
  }
}

export function buildSectionTree(elements: SnapshotElement[]): Section[] {
  // Reset and populate dismiss-sentinel pairing state for this tree only.
  sentinelRefsToSkip = new Set();
  dialogDismissByRef = new Map();
  rowToggleRefsToSkip = new Set();
  findDismissSentinelPairs(elements, sentinelRefsToSkip, dialogDismissByRef);

  const sections: Section[] = [];

  for (const element of elements) {
    const section = buildSection(element, 0);
    if (section) {
      sections.push(section);
    }
  }

  markBackgroundSections(sections);
  applyFilterMerge(sections);

  return sections;
}

export function markMatchingSections(sections: Section[], matchedRefs: Set<string>): void {
  for (const section of sections) {
    const hasDirectMatch =
      section.elements.some((el) => matchedRefs.has(el.ref)) ||
      (section.gridInfo
        ? Array.from(matchedRefs).some((ref) => gridContainsRef(section.gridInfo!, ref))
        : false);

    markMatchingSections(section.subsections, matchedRefs);

    const hasSubsectionMatch = section.subsections.some((s) => s.hasSearchMatch);

    if (hasDirectMatch || hasSubsectionMatch) {
      section.hasSearchMatch = true;
      section.collapsed = false;
    }
  }
}

export function sortSectionsByFocus(sections: Section[]): {
  sorted: Section[];
  dialogStack: string[];
} {
  const dialogStack: string[] = [];

  function processLevel(levelSections: Section[]): Section[] {
    for (const section of levelSections) {
      section.subsections = processLevel(section.subsections);
    }

    const dialogs = levelSections.filter((s) => DIALOG_ROLES.has(s.role));
    const nonDialogs = levelSections.filter((s) => !DIALOG_ROLES.has(s.role));

    const focusedDialog = dialogs.find((d) => d.containsActive);
    if (focusedDialog) {
      focusedDialog.isFocused = true;
      dialogStack.unshift(focusedDialog.heading);
    }

    for (const dialog of dialogs) {
      // Inert dialogs (collapsed/off-screen chat or announcer widgets) aren't
      // real layers on top of the page — keep them out of the stack header.
      if (!dialog.containsActive && !dialog.isInertDialog) {
        dialogStack.push(dialog.heading);
      }
    }

    const sortedDialogs = dialogs.sort((a, b) => {
      if (a.containsActive && !b.containsActive) return -1;
      if (!a.containsActive && b.containsActive) return 1;
      return 0;
    });

    return [...sortedDialogs, ...nonDialogs];
  }

  const sorted = processLevel(sections);
  return { sorted, dialogStack };
}

export function expandSectionByRef(sections: Section[], targetRef: string): boolean {
  for (const section of sections) {
    if (section.ref === targetRef) {
      section.collapsed = false;
      return true;
    }
    if (expandSectionByRef(section.subsections, targetRef)) {
      return true;
    }
  }
  return false;
}

export function findSectionByRef(sections: Section[], targetRef: string): Section | null {
  for (const section of sections) {
    if (section.ref === targetRef) {
      return section;
    }
    const found = findSectionByRef(section.subsections, targetRef);
    if (found) {
      return found;
    }
  }
  return null;
}

export { composeTriggerLabel };
